/**
 * GenPage — reusable content-generation layout.
 *
 * Props:
 *   title           String  — page heading
 *   contentType     String  — PromptRegistry key sent to the API
 *   description     String  — subtitle shown below the heading
 *   extraOptions    ReactNode (optional) — extra controls rendered in the options bar
 *   extraInstructions String (optional) — appended to customInstructions for the API call
 *   outputLabel     String  — label above the output textarea (default "Generated Output")
 *   showDurationSelector Boolean (optional, default false) — renders a duration/word-count
 *                    preset dropdown (see constants/durations.js) and sends the selected
 *                    { duration, wordCount } with every generate call. Used by the Program
 *                    formats (Burning Issue, TCR, World News); every other page is
 *                    unaffected since the prop defaults to false.
 *   programMode     Boolean (optional, default false) — the Program tabs (Burning Issue,
 *                    TCR, World News, Crime Story). Writes the way QuickAI does (same model,
 *                    streamed as it's written) from the user's own saved/typed prompt instead
 *                    of the built-in prompt templates, and hides the admin "Prompt used" panel.
 *   hideWebSearch   Boolean (optional, default false) — hides the Web Search toggle and
 *                    never enables fact enrichment. Used by content types whose contract
 *                    is an exact, invent-nothing transformation of the given input (e.g.
 *                    Digital Anchor Visual), where injected web facts would corrupt the
 *                    output; every other page is unaffected since the prop defaults to false.
 *
 * Export: GenPage (default), SegmentControl (named) so page files can use it in extraOptions
 */

import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FiZap,
  FiCopy,
  FiDownload,
  FiX,
  FiChevronDown,
  FiCheck,
  FiAlertCircle,
  FiCode,
  FiEdit3,
  FiFileText,
  FiFile,
  FiSearch,
  FiGlobe,
  FiChevronUp,
  FiExternalLink,
  FiSquare,
  FiMessageSquare,
  FiSettings,
} from 'react-icons/fi';
import { contentService, promptService, programService } from '../../services/apiService';
import { useCustomPrompts, PromptManager, PromptPicker } from './CustomPrompts';
import { useAuthStore, useInputStore } from '../../utils/store';
import { useT } from '../../utils/i18n';
import { OutputToolbar } from './OutputToolbar';
import { ToneSelector } from './ToneSelector';
import { DEFAULT_TONE_BY_CONTENT_TYPE } from '../../constants/tones';
import { PROGRAM_DURATION_PRESETS, DEFAULT_PROGRAM_DURATION_MINUTES } from '../../constants/durations';

// ─── SegmentControl ─────────────────────────────────────────────────────────
// Exported so page files can use it inside extraOptions prop.

export const SegmentControl = ({ options, labels, value, onChange, disabled = false }) => (
  <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs">
    {options.map((opt, idx) => (
      <button
        key={opt}
        disabled={disabled}
        onClick={() => onChange(opt)}
        className={`px-3 py-1.5 font-medium transition-colors ${
          value === opt
            ? 'bg-primary-500 text-white'
            : 'bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40'
        }`}
      >
        {labels ? labels[idx] : opt}
      </button>
    ))}
  </div>
);

// ─── GenPage ─────────────────────────────────────────────────────────────────

export const GenPage = ({
  title,
  contentType,
  description,
  extraOptions,
  extraInstructions = '',
  outputLabel = 'Generated Output',
  showDurationSelector = false,
  hideWebSearch = false,
  programMode = false,
}) => {
  const { user } = useAuthStore();
  const { globalInputText, setGlobalInputText, clearGlobalInputText } = useInputStore();
  const navigate = useNavigate();
  const t = useT();

  const input = globalInputText;
  const setInput = setGlobalInputText;

  const [output, setOutput]             = useState('');
  const [tone, setTone]                 = useState(() => DEFAULT_TONE_BY_CONTENT_TYPE[contentType] || 'authoritative');
  const [loading, setLoading]           = useState(false);
  const [loadingStep, setLoadingStep]   = useState(null); // null | 'searching' | 'generating'
  const [error, setError]               = useState('');
  const [errorType, setErrorType]       = useState(''); // 'budget' | 'api' | ''
  const [copySuccess, setCopySuccess]   = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [promptOpen, setPromptOpen]     = useState(false);
  const [promptData, setPromptData]     = useState(null);
  const [promptLoading, setPromptLoading] = useState(false);
  const [enrichedFacts, setEnrichedFacts] = useState([]);
  const [factsOpen, setFactsOpen]       = useState(false);
  const [webSearch, setWebSearch]       = useState(!hideWebSearch);
  const [durationMinutes, setDurationMinutes] = useState(DEFAULT_PROGRAM_DURATION_MINUTES);

  // Output toolbar — font controls, edit/feedback-loop state
  const [fontSize, setFontSize]         = useState(16);
  const [fontFamily, setFontFamily]     = useState('Noto Sans Telugu');
  const [isEditing, setIsEditing]       = useState(false);
  const [editedText, setEditedText]     = useState('');
  const [originalAiText, setOriginalAiText] = useState('');
  const [savedEditText, setSavedEditText]   = useState('');
  const [hasUnsavedEdit, setHasUnsavedEdit] = useState(false);
  const [contentId, setContentId]       = useState(null);

  // "Input carried over from previous tab" hint — shown only once per mount,
  // when this page/tab is freshly mounted and already has global input text.
  const [showCarriedOverHint, setShowCarriedOverHint] = useState(false);
  const hasMountedRef = useRef(false);

  // Program tabs: which of the user's prompts to write with. There is always at
  // least the built-in standard prompt, and one of them is the default.
  const { prompts, limits: promptLimits, reload: reloadPrompts } =
    useCustomPrompts(programMode ? contentType : null);
  const [selectedPromptId, setSelectedPromptId] = useState('');
  const [promptManagerOpen, setPromptManagerOpen] = useState(false);
  const abortRef = useRef(null);

  const outputRef    = useRef(null);
  const outputBoxRef = useRef(null);
  // While a script streams in, the output box follows the newest words — the
  // box is a fixed height, so without this the text looked like it had
  // stopped once it reached the bottom. Scrolling up to read stops following.
  const followOutputRef = useRef(true);
  const downloadRef  = useRef(null);
  const editableRef  = useRef(null);
  const stepTimerRef = useRef(null);

  // Close download dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (downloadRef.current && !downloadRef.current.contains(e.target)) {
        setDownloadOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // On fresh mount only — if there's already text in the global input store,
  // show a one-time hint that it was carried over from another page/tab.
  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      if (globalInputText) {
        setShowCarriedOverHint(true);
      }
    }
  }, []);

  // Clear output when contentType changes (page reuse)
  useEffect(() => {
    setOutput('');
    setError('');
    setErrorType('');
    setPromptData(null);
    setPromptOpen(false);
    setIsEditing(false);
    setEditedText('');
    setOriginalAiText('');
    setSavedEditText('');
    setHasUnsavedEdit(false);
    setContentId(null);
    setEnrichedFacts([]);
    setFactsOpen(false);
    setTone(DEFAULT_TONE_BY_CONTENT_TYPE[contentType] || 'authoritative');
    setDurationMinutes(DEFAULT_PROGRAM_DURATION_MINUTES);
    setSelectedPromptId('');
  }, [contentType]);

  // Program tabs: select the default prompt — on load, whenever the user makes
  // a different prompt the default, and when the selected one is deleted.
  const defaultPromptId = prompts.find((p) => p.isDefault)?.id || prompts[0]?.id || '';
  useEffect(() => {
    if (programMode && defaultPromptId) setSelectedPromptId(defaultPromptId);
  }, [programMode, defaultPromptId]);
  useEffect(() => {
    if (programMode && prompts.length && !prompts.some((p) => p.id === selectedPromptId)) {
      setSelectedPromptId(defaultPromptId);
    }
  }, [programMode, prompts, selectedPromptId, defaultPromptId]);

  useEffect(() => {
    const el = outputBoxRef.current;
    if (loading && programMode && el && followOutputRef.current) el.scrollTop = el.scrollHeight;
  }, [output, loading, programMode]);

  // Stop a running stream when leaving the page.
  useEffect(() => () => abortRef.current?.abort(), []);

  // Populate the contentEditable div imperatively when editing starts.
  // The div has NO JSX children — putting editedText there causes React's reconciliation
  // to overwrite the DOM on every keystroke, resetting the cursor to position 0.
  // useLayoutEffect fires synchronously after DOM mutation but before the browser paints,
  // so there is no flash of empty content.
  useLayoutEffect(() => {
    if (isEditing && editableRef.current) {
      editableRef.current.innerText = editedText;
      editableRef.current.focus();
      const range = document.createRange();
      const sel = window.getSelection();
      range.selectNodeContents(editableRef.current);
      range.collapse(false);
      sel.removeAllRanges();
      sel.addRange(range);
    }
  }, [isEditing]); // editedText intentionally omitted — fires only when isEditing toggles

  // ── Handlers ────────────────────────────────────────────────────────────────

  const showGenerationError = ({ message = '', code, status } = {}) => {
    const isBudget = status === 429 || code === 'BUDGET_EXCEEDED' || /daily budget/i.test(message) || /budget.*reached/i.test(message);
    const isApiKey = /api key/i.test(message) || /not configured/i.test(message);
    if (isBudget) { setErrorType('budget'); setError(''); }
    else if (isApiKey) { setErrorType('api'); setError(''); }
    else { setErrorType(''); setError(message || t('genpage.generateError')); }
  };

  // Program tabs: stream the script in as it's written (see ProgramGenerationService).
  const handleGenerateProgram = async () => {
    const searchEnabled = webSearch;
    const selectedDuration = PROGRAM_DURATION_PRESETS.find((p) => p.minutes === durationMinutes);
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setLoadingStep(searchEnabled ? 'searching' : 'generating');
    setError('');
    setErrorType('');
    setEnrichedFacts([]);
    setFactsOpen(false);
    setOutput('');
    setIsEditing(false);
    setContentId(null);
    setTimeout(() => outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }), 80);

    let text = '';
    let finished = false;
    followOutputRef.current = true;
    await programService.stream(
      {
        contentType,
        inputContent: input,
        instructions: (prompts.find((p) => p.id === selectedPromptId)?.text || '').trim(),
        tone,
        enableWebSearch: searchEnabled,
        ...(selectedDuration ? { duration: selectedDuration.minutes, wordCount: selectedDuration.words } : {}),
      },
      {
        signal: controller.signal,
        onStatus: ({ step }) => setLoadingStep(step === 'searching' ? 'searching' : 'generating'),
        onChunk: ({ text: piece }) => {
          text += piece;
          setOutput(text);
        },
        onDone: ({ contentId: id, enrichedFacts: facts = [] }) => {
          finished = true;
          setContentId(id || null);
          setEnrichedFacts(facts);
          if (facts.length) setFactsOpen(true);
        },
        onError: showGenerationError,
      }
    );

    const final = text.trim();
    setOutput(final);
    setOriginalAiText(final);
    setEditedText(final);
    setSavedEditText('');
    setHasUnsavedEdit(false);
    if (!finished && controller.signal.aborted && final) toast.info(t('genpage.stopped'));
    abortRef.current = null;
    setLoading(false);
    setLoadingStep(null);
  };

  const handleStop = () => abortRef.current?.abort();

  const handleGenerate = async () => {
    if (!input.trim()) {
      setError(t('genpage.inputRequired'));
      return;
    }
    if (programMode) return handleGenerateProgram();
    // Capture at call-time to avoid stale closure issues across the async await
    const searchEnabled = webSearch;

    setLoading(true);
    setLoadingStep(searchEnabled ? 'searching' : 'generating');
    setError('');
    setErrorType('');
    setEnrichedFacts([]);
    setFactsOpen(false);

    if (searchEnabled) {
      stepTimerRef.current = setTimeout(() => setLoadingStep('generating'), 5000);
    }

    try {
      const parts = [`Tone: ${tone}`];
      if (extraInstructions) parts.push(extraInstructions);
      const customInstructions = parts.join('\n');

      const selectedDuration = showDurationSelector
        ? PROGRAM_DURATION_PRESETS.find((p) => p.minutes === durationMinutes)
        : null;

      const result = await contentService.generateContent({
        contentType,
        inputContent: input,
        tone,
        customInstructions,
        language: 'telugu',
        enableWebSearch: searchEnabled,
        ...(selectedDuration
          ? { duration: selectedDuration.minutes, wordCount: selectedDuration.words }
          : {}),
      });
      const text  = result?.outputs?.primaryOutput || '';
      // Only show enriched facts if web search was explicitly enabled for this run
      const facts = searchEnabled ? (result?.enrichedFacts || []) : [];
      setOutput(text);
      setOriginalAiText(text);
      setEditedText(text);
      setSavedEditText('');
      setHasUnsavedEdit(false);
      setIsEditing(false);
      setContentId(result?._id || null);
      setEnrichedFacts(facts);
      if (facts.length > 0) setFactsOpen(true);
      setTimeout(() => {
        outputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);
    } catch (e) {
      const status   = e.response?.status;
      const msg      = e.response?.data?.message || e.message || '';
      const isBudget = status === 429 || /daily budget/i.test(msg) || /budget.*reached/i.test(msg);
      const isApiKey = /api key/i.test(msg) || /no api key/i.test(msg) || /not configured/i.test(msg);
      if (isBudget) {
        setErrorType('budget');
        setError('');
      } else if (isApiKey) {
        setErrorType('api');
        setError('');
      } else {
        setErrorType('');
        setError(msg || t('genpage.generateError'));
      }
    } finally {
      clearTimeout(stepTimerRef.current);
      setLoading(false);
      setLoadingStep(null);
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (_) {
      setError(t('genpage.clipboardError'));
    }
  };

  const handleDownload = async (format) => {
    if (!output) return;
    setDownloadOpen(false);
    const docTitle = `${title} — ${new Date().toLocaleDateString('en-IN')}`;
    try {
      const blob = await contentService.downloadContent({ text: output, title: docTitle, format });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title.replace(/\s+/g, '-').toLowerCase()}.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_) {
      setError(t('genpage.downloadError'));
    }
  };

  const handleTogglePrompt = () => {
    setPromptOpen((v) => !v);
  };

  const handleClear = () => {
    clearGlobalInputText();
    setOutput('');
    setError('');
    setIsEditing(false);
    setEditedText('');
    setOriginalAiText('');
    setSavedEditText('');
    setHasUnsavedEdit(false);
    setContentId(null);
    setShowCarriedOverHint(false);
  };

  // ── Output toolbar handlers ─────────────────────────────────────────────────

  const handleToggleEdit = () => {
    if (!output) return;
    setEditedText(output);
    setIsEditing(true);
  };

  const handleEditableInput = () => {
    const text = editableRef.current?.innerText ?? '';
    setEditedText(text);
    setHasUnsavedEdit(text !== originalAiText);
  };

  const handleDiscardEdit = () => {
    setEditedText(originalAiText);
    setOutput(originalAiText);
    setIsEditing(false);
    setHasUnsavedEdit(false);
  };

  const handleSaveEdit = async () => {
    const finalText = editableRef.current?.innerText ?? editedText;
    setOutput(finalText);
    setEditedText(finalText);
    setSavedEditText(finalText);
    setIsEditing(false);
    setHasUnsavedEdit(false);

    try {
      await contentService.saveEditFeedback({
        ...(contentId && { contentGenerationId: contentId }),
        contentType,
        inputText: input,
        aiOutput: originalAiText,
        userEdit: finalText,
      });
      toast.success(t('genpage.editSaved'));
    } catch (e) {
      toast.error(
        e.response?.data?.message ||
        e.message ||
        t('genpage.editSavedError')
      );
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const wordCount = (str) => str.trim().split(/\s+/).filter(Boolean).length;

  // Admins only: fetch the prompt used for this content type on mount (and whenever
  // contentType changes), so the "Prompt used" section has data ready without a
  // click-triggered round trip. Never fetched for non-admins — the section is not
  // rendered for them at all (see below), and GET /api/prompts/:type would only
  // return label/notes for a non-admin anyway.
  useEffect(() => {
    if (!isAdmin || programMode) {
      setPromptData(null);
      return;
    }
    let cancelled = false;
    setPromptLoading(true);
    promptService.getPrompt(contentType)
      .then((data) => { if (!cancelled) setPromptData(data); })
      .catch(() => { if (!cancelled) setPromptData(null); })
      .finally(() => { if (!cancelled) setPromptLoading(false); });
    return () => { cancelled = true; };
  }, [contentType, isAdmin, programMode]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-4">

      {/* Page header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{title}</h1>
        {description && (
          <p className="text-sm text-gray-500 mt-0.5">{description}</p>
        )}
      </div>

      {/* Options / controls bar */}
      <div className="flex items-center gap-2 flex-wrap">

        {/* Generate (Program tabs: Stop while the script is streaming in) */}
        {programMode && loading ? (
          <button
            onClick={handleStop}
            className="btn border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 flex items-center gap-2 min-w-[130px] justify-center"
          >
            <FiSquare size={13} />
            {t('genpage.stop')}
          </button>
        ) : (
        <button
          onClick={handleGenerate}
          disabled={loading}
          className="btn btn-primary flex items-center gap-2 min-w-[130px] justify-center"
        >
          {loading ? (
            <>
              <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {loadingStep === 'searching' ? 'Searching Facts…' : t('genpage.generating')}
            </>
          ) : (
            <>
              <FiZap size={14} />
              {t('genpage.generate')}
            </>
          )}
        </button>
        )}

        <div className="h-5 w-px bg-gray-200 mx-1" />

        {/* Tone */}
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs text-gray-500 font-medium whitespace-nowrap">{t('genpage.tone')}</span>
          <ToneSelector
            contentType={contentType}
            value={tone}
            onChange={setTone}
          />
        </div>

        {!hideWebSearch && (
          <>
            <div className="h-5 w-px bg-gray-200 mx-1" />

            {/* Web Search toggle */}
            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={webSearch}
                onChange={e => setWebSearch(e.target.checked)}
                disabled={loading}
                className="w-3.5 h-3.5 rounded accent-primary-500 cursor-pointer disabled:opacity-40"
              />
              <span className="flex items-center gap-1 text-xs text-gray-600 font-medium whitespace-nowrap">
                <FiGlobe size={12} className={webSearch ? 'text-primary-500' : 'text-gray-400'} />
                Web Search
              </span>
            </label>
          </>
        )}

        {/* Duration / word-count preset — Program formats only (Burning Issue, TCR, World News) */}
        {showDurationSelector && (
          <>
            <div className="h-5 w-px bg-gray-200 mx-1" />
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-xs text-gray-500 font-medium whitespace-nowrap">Duration</span>
              <select
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(Number(e.target.value))}
                disabled={loading}
                className="input text-xs py-2 pr-7 pl-2.5 min-w-[180px]"
              >
                {PROGRAM_DURATION_PRESETS.map((preset) => (
                  <option key={preset.minutes} value={preset.minutes}>
                    {preset.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        )}

        {/* Extra options slot */}
        {extraOptions && (
          <>
            <div className="h-5 w-px bg-gray-200 mx-1" />
            {extraOptions}
          </>
        )}

        {/* Right-side actions */}
        <div className="ml-auto flex items-center gap-1.5">

          {/* Copy */}
          <button
            onClick={handleCopy}
            disabled={!output}
            title={t('genpage.copyToClipboard')}
            className="btn btn-ghost flex items-center gap-1.5 text-sm disabled:opacity-40"
          >
            {copySuccess
              ? <FiCheck size={14} className="text-green-500" />
              : <FiCopy size={14} />}
            <span>{copySuccess ? t('genpage.copied') : t('genpage.copy')}</span>
          </button>

          {/* Download dropdown */}
          <div className="relative" ref={downloadRef}>
            <button
              onClick={() => setDownloadOpen((v) => !v)}
              disabled={!output}
              title={t('genpage.download')}
              className="btn btn-ghost flex items-center gap-1.5 text-sm disabled:opacity-40"
            >
              <FiDownload size={14} />
              <span>{t('genpage.download')}</span>
              <FiChevronDown
                size={12}
                className={`text-gray-400 transition-transform ${downloadOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {downloadOpen && (
              <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden z-20">
                <button
                  onClick={() => handleDownload('docx')}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FiFileText size={13} className="text-blue-500 flex-shrink-0" />
                  <span>{t('genpage.docx')}</span>
                </button>
                <button
                  onClick={() => handleDownload('pdf')}
                  className="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition"
                >
                  <FiFile size={13} className="text-red-500 flex-shrink-0" />
                  <span>{t('genpage.pdf')}</span>
                </button>
              </div>
            )}
          </div>

          {/* Clear */}
          <button
            onClick={handleClear}
            title={t('genpage.clearAll')}
            className="btn btn-ghost flex items-center gap-1.5 text-sm text-gray-500"
          >
            <FiX size={14} />
            <span>{t('genpage.clear')}</span>
          </button>
        </div>
      </div>

      {/* Budget-exhausted banner */}
      {errorType === 'budget' && (
        <div className="flex items-start gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3">
          <FiAlertCircle size={18} className="mt-0.5 text-orange-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-orange-800">{t('alert.budgetExhausted')}</p>
            <p className="text-xs text-orange-700 mt-0.5">{t('alert.budgetExhaustedDetail')}</p>
          </div>
          <button onClick={() => setErrorType('')} className="text-orange-400 hover:text-orange-600 flex-shrink-0">
            <FiX size={14} />
          </button>
        </div>
      )}

      {/* API-not-configured banner */}
      {errorType === 'api' && (
        <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
          <FiAlertCircle size={18} className="mt-0.5 text-yellow-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-yellow-900">{t('alert.apiNotConfigured')}</p>
            <p className="text-xs text-yellow-800 mt-0.5">{t('alert.apiNotConfiguredDetail')}</p>
          </div>
          <button onClick={() => setErrorType('')} className="text-yellow-500 hover:text-yellow-700 flex-shrink-0">
            <FiX size={14} />
          </button>
        </div>
      )}

      {/* Generic error banner */}
      {error && (
        <div className="flex items-start gap-2 text-sm text-danger-600 bg-danger-50 border border-danger-200 rounded-xl px-3 py-2.5">
          <FiAlertCircle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
          <button
            onClick={() => setError('')}
            className="ml-auto text-danger-400 hover:text-danger-600"
          >
            <FiX size={12} />
          </button>
        </div>
      )}

      {/* Two-column editor — single column on mobile/tablet, side-by-side on desktop */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 3xl:max-w-7xl 3xl:mx-auto">

        {/* Input */}
        <div className="flex flex-col gap-1.5">
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            {t('genpage.inputLabel')}
          </label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t('genpage.inputPlaceholder')}
            className="input w-full resize-y font-sans text-sm leading-relaxed genpage-textarea"
          />
          <div className="flex justify-between text-xs text-gray-400">
            <span>{input.length > 0 ? `${input.length} ${t('genpage.characters')}` : ''}</span>
            <span>{input.length > 0 ? `${wordCount(input)} ${t('genpage.words')}` : ''}</span>
          </div>
          {showCarriedOverHint && (
            <div className="inline-flex items-center gap-1.5 self-start text-[11px] text-gray-500 bg-gray-100 border border-gray-200 rounded-full px-2.5 py-1">
              <FiCheck size={11} className="text-gray-400" />
              <span>Input carried over from previous tab</span>
              <button
                onClick={() => setShowCarriedOverHint(false)}
                title="Dismiss"
                className="text-gray-400 hover:text-gray-600 leading-none"
              >
                <FiX size={11} />
              </button>
            </div>
          )}
        </div>

        {/* Output */}
        <div className="flex flex-col gap-1.5" ref={outputRef}>
          <label className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
            {outputLabel}
          </label>

          <OutputToolbar
            fontSize={fontSize}
            setFontSize={setFontSize}
            fontFamily={fontFamily}
            setFontFamily={setFontFamily}
            isEditing={isEditing}
            onToggleEdit={handleToggleEdit}
            onSaveEdit={handleSaveEdit}
            onDiscardEdit={handleDiscardEdit}
            hasUnsavedEdit={hasUnsavedEdit}
            hasSavedEdit={!!savedEditText}
            originalText={originalAiText}
            editedText={savedEditText || editedText}
          />

          <div className="relative flex-1">
            {isEditing ? (
              <div
                ref={editableRef}
                contentEditable
                suppressContentEditableWarning
                onInput={handleEditableInput}
                className="input w-full resize-y leading-relaxed bg-white text-gray-800 overflow-auto whitespace-pre-wrap focus:outline-none focus:ring-2 focus:ring-primary-500 genpage-textarea"
                style={{ fontFamily, fontSize: `${fontSize}px` }}
              ></div>
            ) : (
              <textarea
                ref={outputBoxRef}
                onScroll={(e) => {
                  const el = e.currentTarget;
                  followOutputRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
                }}
                readOnly
                value={output}
                placeholder={loading ? t('genpage.aiGenerating') : t('genpage.outputPlaceholder')}
                className="input w-full resize-y leading-relaxed bg-gray-50/80 text-gray-800 output-area genpage-textarea"
                style={{ fontFamily, fontSize: `${fontSize}px` }}
              />
            )}
            {/* Program tabs stream text in, so the overlay only covers the wait before the first words */}
            {loading && !(programMode && output) && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/70 rounded-xl backdrop-blur-[2px]">
                <div className="flex flex-col items-center gap-3 px-4">
                  <div className="w-9 h-9 border-[3px] border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <div className="flex items-center gap-4">
                    {webSearch && (
                      <>
                        <div className={`flex items-center gap-1.5 text-xs font-medium ${loadingStep === 'searching' ? 'text-primary-600' : 'text-gray-400'}`}>
                          <FiSearch size={11} />
                          <span>Searching facts</span>
                        </div>
                        <div className="w-6 h-px bg-gray-300" />
                      </>
                    )}
                    <div className="flex items-center gap-1.5 text-xs font-medium text-primary-600">
                      <FiZap size={11} />
                      <span>Generating</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          {output && !loading && (
            <div className="flex justify-between text-xs text-gray-400">
              <span>{output.length} {t('genpage.characters')}</span>
              <span>{wordCount(output)} {t('genpage.words')}</span>
            </div>
          )}
        </div>
      </div>

      {/* Enriched facts panel — shown after generation when web facts were found */}
      {enrichedFacts.length > 0 && !loading && (
        <div className="border border-blue-200 rounded-xl overflow-hidden">
          <button
            onClick={() => setFactsOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 hover:bg-blue-100 transition text-sm font-medium text-blue-800"
          >
            <span className="flex items-center gap-2">
              <FiGlobe size={14} className="text-blue-500" />
              <span>{enrichedFacts.length} additional facts found via web search</span>
              <span className="text-xs font-normal text-blue-500">(used to enrich this output)</span>
            </span>
            {factsOpen
              ? <FiChevronUp size={14} className="text-blue-400" />
              : <FiChevronDown size={14} className="text-blue-400" />}
          </button>
          {factsOpen && (
            <div className="px-4 py-3 border-t border-blue-200 bg-white space-y-2">
              {enrichedFacts.map((fact, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm">
                  <span className="mt-0.5 flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 text-blue-600 text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-700 leading-snug">{fact.text}</p>
                    {fact.source && (
                      <a
                        href={fact.source}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-blue-500 hover:underline mt-0.5"
                      >
                        <FiExternalLink size={10} />
                        {fact.title || fact.source}
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Program tabs: which prompt to write with, and Manage prompts (add, edit, delete, default) */}
      {programMode && (
        <div className="flex flex-wrap items-center gap-2 3xl:max-w-7xl 3xl:mx-auto">
          <label htmlFor="program-prompt" className="flex items-center gap-1.5 text-xs text-gray-500 font-medium whitespace-nowrap">
            <FiMessageSquare size={12} />
            {t('prompts.label')}
          </label>
          <PromptPicker
            id="program-prompt"
            prompts={prompts}
            value={selectedPromptId}
            onChange={setSelectedPromptId}
            disabled={loading || !prompts.length}
            className="min-w-0 flex-1 sm:flex-none sm:w-72"
            buttonClassName="input text-xs py-2 px-2.5 w-full flex items-center justify-between gap-2 text-left disabled:opacity-50"
          />
          <button
            type="button"
            onClick={() => setPromptManagerOpen(true)}
            disabled={loading}
            className="btn btn-ghost flex items-center gap-1.5 text-xs disabled:opacity-40"
          >
            <FiSettings size={12} />
            {t('prompts.manageButton')}
          </button>
        </div>
      )}

      {promptManagerOpen && (
        <PromptManager
          scope={contentType}
          title={t('prompts.managerTitle', { place: title })}
          prompts={prompts}
          limits={promptLimits}
          alwaysDefault
          onChanged={reloadPrompts}
          onClose={() => setPromptManagerOpen(false)}
        />
      )}

      {/* Collapsible "Prompt used" section — admin/superadmin only, entire section hidden otherwise.
          Not on Program tabs, which don't use built-in prompts. */}
      {isAdmin && !programMode && (
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <button
            onClick={handleTogglePrompt}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition text-sm font-medium text-gray-700"
          >
            <span className="flex items-center gap-2">
              <FiCode size={14} className="text-gray-400" />
              {t('genpage.promptUsedFor')}{' '}
              <span className="text-primary-600">{title}</span>
            </span>
            <FiChevronDown
              size={14}
              className={`text-gray-400 transition-transform duration-200 ${promptOpen ? 'rotate-180' : ''}`}
            />
          </button>

          {promptOpen && (
            <div className="px-4 py-3 space-y-3 border-t border-gray-200 bg-white">
              {promptLoading ? (
                <p className="text-xs text-gray-400">{t('genpage.loadingPrompt')}</p>
              ) : promptData ? (
                <>
                  <div>
                    <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest mb-1.5">
                      {t('genpage.userPromptTemplate')}
                    </p>
                    <pre className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3 overflow-x-auto whitespace-pre-wrap font-mono leading-relaxed border border-gray-100">
                      {promptData.userPromptTemplate || t('genpage.noTemplate')}
                    </pre>
                  </div>
                  <div className="flex items-start justify-between gap-3">
                    {promptData.notes && (
                      <p className="text-xs text-gray-500 italic flex-1">{promptData.notes}</p>
                    )}
                    <button
                      onClick={() => navigate(`/settings/prompt-editor?type=${contentType}`)}
                      className="flex-shrink-0 flex items-center gap-1 text-xs text-primary-600 hover:underline font-medium"
                    >
                      <FiEdit3 size={11} />
                      {t('genpage.editPrompt')}
                    </button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-gray-400">
                  {t('genpage.promptError')}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
